import {
  hasRequiredCategorizedUploads,
  hasUntaggedLegacyUpload,
} from 'tio-common/utils/tuition-assistance/categorized-upload';
import type {
  CategorizedUploadAssetLike,
  DocumentEntryLike,
} from 'tio-common/utils/tuition-assistance/categorized-upload';

export interface CategorizedUploadConfig {
  /** Company capability, enabled unless explicitly false; independent of browser beta settings. */
  isAiSummariesEnabled: boolean | undefined;
  /** The template's configured rows for THIS classification. */
  documentEntries: readonly DocumentEntryLike[];
}

/**
 * Whether the categorized (row-per-document-type) uploader is what renders,
 * as opposed to the legacy free-form one. The submit gate and the rendered
 * component must answer this identically — if the gate assumed rows that were
 * never drawn, the employee would face a permanently disabled Submit.
 */
export function usesCategorizedUpload({
  isAiSummariesEnabled,
  documentEntries,
}: CategorizedUploadConfig): boolean {
  if (isAiSummariesEnabled === false) {
    return false;
  }
  return documentEntries.length > 0;
}

export interface RequiredDocumentsInput extends CategorizedUploadConfig {
  /**
   * Whether documents are required at all for this step — the evidence surface
   * passes `application.isEvidenceRequired`, which already honors the
   * per-application waiver. Rows alone are NOT a requirement: a template that
   * lists rows with the requirement off stays submittable.
   */
  isRequired: boolean;
  /** `COMPLETION_DOCUMENTS` on the evidence surface; the show page is phase-aware. */
  classification: string;
  /** The assets the uploader itself renders from (application-level). */
  assets: readonly CategorizedUploadAssetLike[];
  /**
   * Presence fallback for the free-form branch — the model's own
   * `hasRequiredEvidenceAttachments`, which checks the application level or
   * every active course. Only consulted when no categorized rows are configured.
   */
  hasLegacyRequiredAttachments: boolean;
}

/** Returns whether required documents are present; AI verdicts are advisory because pending results can be orphaned. */
export function hasRequiredDocuments(input: RequiredDocumentsInput): boolean {
  if (!input.isRequired) {
    return true;
  }

  if (usesCategorizedUpload(input)) {
    if (hasRequiredCategorizedUploads(input.assets, input.classification, input.documentEntries)) {
      return true;
    }
    // Untagged carryover predates configured rows, so one file can unlock submission even with empty rows.
    // TODO: narrow this exception now that files can be reclassified, preserving existing applications across all evidence surfaces.
    return hasUntaggedLegacyUpload(input.assets, input.classification);
  }

  return input.hasLegacyRequiredAttachments;
}
